import {
  type RunningTimerResponse,
  type TimeEntry,
  type TimeEntryListQuery,
  type TimeEntryListResponse,
  type TimeEntryManualCreate,
  type TimeEntryUpdate,
  type TimerStart,
  type UserTimeSettings,
  type UserTimeSettingsUpdate,
} from "@l30/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetchUnchecked } from "./client.js";

/**
 * Time tracking — TanStack Query hooks.
 *
 * The floating timer widget polls `useRunningTimer` every 30 s so a
 * timer started in another tab / device shows up. The page-level
 * surfaces (timesheet, entries list) listen on the same key and
 * invalidate together when start/stop fires.
 */

const RUNNING_KEY = ["time-tracking", "running"] as const;
const ENTRIES_KEY = ["time-tracking", "entries"] as const;
const SETTINGS_KEY = ["time-tracking", "settings"] as const;

// ─── Running timer ───────────────────────────────────────

export function useRunningTimer() {
  return useQuery<RunningTimerResponse>({
    queryKey: RUNNING_KEY,
    queryFn: () => apiFetchUnchecked("/time-tracking/running"),
    refetchInterval: 30_000,
  });
}

export function useStartTimer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: TimerStart) =>
      apiFetchUnchecked<TimeEntry>("/time-tracking/start", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: RUNNING_KEY });
      void qc.invalidateQueries({ queryKey: ENTRIES_KEY });
    },
  });
}

export function useStopTimer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetchUnchecked<TimeEntry>("/time-tracking/stop", {
        method: "POST",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: RUNNING_KEY });
      void qc.invalidateQueries({ queryKey: ENTRIES_KEY });
    },
  });
}

// ─── Entries CRUD ────────────────────────────────────────

export function useTimeEntries(query: TimeEntryListQuery) {
  return useQuery<TimeEntryListResponse>({
    queryKey: [...ENTRIES_KEY, query],
    queryFn: () => {
      const params = new URLSearchParams({
        fromAt: query.fromAt,
        toAt: query.toAt,
      });
      if (query.projectId) params.set("projectId", query.projectId);
      if (query.taskId) params.set("taskId", query.taskId);
      if (query.userEmail) params.set("userEmail", query.userEmail);
      return apiFetchUnchecked(`/time-tracking/entries?${params.toString()}`);
    },
  });
}

export function useCreateManualEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: TimeEntryManualCreate) =>
      apiFetchUnchecked<TimeEntry>("/time-tracking/entries", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ENTRIES_KEY }),
  });
}

export function useUpdateEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: TimeEntryUpdate }) =>
      apiFetchUnchecked<TimeEntry>(`/time-tracking/entries/${id}`, {
        method: "PUT",
        body: JSON.stringify(patch),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ENTRIES_KEY }),
  });
}

export function useDeleteEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetchUnchecked<void>(`/time-tracking/entries/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ENTRIES_KEY }),
  });
}

// ─── Settings ────────────────────────────────────────────

export function useUserTimeSettings() {
  return useQuery<UserTimeSettings>({
    queryKey: SETTINGS_KEY,
    queryFn: () => apiFetchUnchecked("/time-tracking/settings"),
  });
}

export function useUpdateUserTimeSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: UserTimeSettingsUpdate) =>
      apiFetchUnchecked<UserTimeSettings>("/time-tracking/settings", {
        method: "PUT",
        body: JSON.stringify(patch),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: SETTINGS_KEY }),
  });
}
